import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { Spinner } from './Spinner';
import { INTERACTIVE_CLASSES, VARIANT_CLASSES, type ButtonVariant } from './buttonStyles';

export type { ButtonVariant };
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    loading?: boolean;
    fullWidth?: boolean;
    leadingIcon?: ReactNode;
    trailingIcon?: ReactNode;
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
    sm: 'h-7 px-2.5 text-label',
    md: 'h-9 px-3.5 text-value',
    lg: 'h-11 px-5 text-body font-bold',
};

const BASE_CLASSES = cn('inline-flex items-center justify-center gap-1.5 rounded-control', INTERACTIVE_CLASSES);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'primary',
        size = 'md',
        loading = false,
        fullWidth = false,
        leadingIcon,
        trailingIcon,
        disabled,
        className,
        children,
        ...rest
    },
    ref,
) {
    return (
        <button
            ref={ref}
            aria-busy={loading || undefined}
            disabled={disabled || loading}
            className={cn(
                BASE_CLASSES,
                VARIANT_CLASSES[variant],
                SIZE_CLASSES[size],
                fullWidth && 'w-full',
                className,
            )}
            {...rest}
        >
            {loading ? <Spinner className="h-3.5 w-3.5" /> : leadingIcon}
            {children}
            {trailingIcon}
        </button>
    );
});

export default Button;
